import { requestGQL } from 'common/graphQlClient';
import { useOfflineMutation } from 'common/offline/useOfflineMutation';

import { MutatePatchFiber, PATCH_FIBER } from '../api';
import { Fiber, Outlet } from '../Outlet';
import { queryClient } from '../../../common/queryClient';
import type { GetPropertyConnectionResponse, LinkData } from '../../connection/api';

export const parseFiberToRequest =
  (outletId: Outlet['id']) =>
  (fiber: Fiber) =>
  (fiberForm: Partial<Fiber>): MutatePatchFiber => {
    return {
      id: fiber.id,
      odfId: fiber.odfId ?? '',
      odfPort: fiber.fiber ?? '',
      outletId,
      outletPort: fiber.toPort ?? '',
      ...(fiberForm.duct ? { duct: fiberForm.duct } : {}),
      ...(fiberForm.length ? { length: fiberForm.length } : {}),
      ...(fiberForm.db1310 ? { db1310: fiberForm.db1310 } : {}),
      ...(fiberForm.db1550 ? { db1550: fiberForm.db1550 } : {}),
    };
  };

type UsePatchFiberOptions = {
  onOfflineQueued?: () => void;
  onSuccess?: () => void;
};

export const usePatchFiber = (
  outletId: string,
  propertyId: number,
  fiber: Fiber,
  options: UsePatchFiberOptions = {},
) => {
  const applyOptimisticUpdate: (v: any) => void = (optimisticData: MutatePatchFiber) => {
    queryClient.setQueryData<GetPropertyConnectionResponse | undefined>(['propertyConnection', propertyId], (old) => {
      if (!old?.propertyById?.connectionByConnectionId.serialized2) return old;
      const updatedFiberIdx = old.propertyById.connectionByConnectionId.serialized2.linkDataArray.findIndex(
        ({ id }) => optimisticData.id === id,
      );
      if (updatedFiberIdx < 0) return old;
      const oldFiberData = old.propertyById.connectionByConnectionId.serialized2.linkDataArray[updatedFiberIdx];
      const oldCloned = structuredClone(old);
      (oldCloned.propertyById.connectionByConnectionId.serialized2.linkDataArray as Array<LinkData>)[updatedFiberIdx] =
        {
          ...oldFiberData,
          cfObject: {
            ...(oldFiberData.cfObject ?? {}),
            duct: optimisticData.duct ?? oldFiberData.cfObject?.duct,
            length: optimisticData.length ?? oldFiberData.cfObject?.length,
            db1310: optimisticData.db1310 ?? oldFiberData.cfObject?.db1310,
            db1550: optimisticData.db1550 ?? oldFiberData.cfObject?.db1550,
          },
        };

      return oldCloned;
    });
  };

  const { data, mutate, mutateAsync, isLoading, isError, isSuccess, isOnline, pendingCount, wasQueuedOffline } =
    useOfflineMutation<string, unknown, Partial<Fiber>>({
      mutationFn: async (payload) =>
        requestGQL(PATCH_FIBER, {
          propertyId,
          fiber: parseFiberToRequest(outletId)(fiber)(payload),
        }),
      mutationName: 'patchFiber',
      entity: 'fiber',
      operationType: 'UPDATE',
      propertyId,
      invalidateKeys: [['propertyConnection', propertyId]],
      getOptimisticData: (v) => v,
      applyOptimisticUpdate,
      onOfflineQueued: options.onOfflineQueued,
      onSuccess: options.onSuccess,
    });

  return {
    data,
    mutate: ({ fiberForm }: { fiberForm: Partial<Fiber> }, opts?: any) =>
      mutate(parseFiberToRequest(outletId)(fiber)(fiberForm) as any, opts),
    mutateAsync: ({ fiberForm }: { fiberForm: Partial<Fiber> }, opts?: any) =>
      mutateAsync(parseFiberToRequest(outletId)(fiber)(fiberForm) as any, opts),
    isLoading,
    isError,
    isSuccess,
    isOnline,
    pendingCount,
    wasQueuedOffline,
  };
};
